import React, {
    useRef,
    useState,
    useEffect,
    useCallback,
    useMemo,
    useContext,
} from 'react';
import * as Cookies from 'js-cookie';
import { useSwipeable } from 'react-swipeable';
import { View, Drawer, Button, useViewport, Icon } from '@fairygodboss/ui';
import {
    useJobSearchContext,
    JOB_CARD_LOADING,
    JOBS_NOT_FOUND,
} from '../../../contexts/JobSearchContext';
import { SingleJobTemplateV2 } from '../SingleJob/SingleJobTemplateV2/SingleJobTemplateV2';
import { JobNotFound } from './JobCard/JobNotFound';
import StickyCompanyHeaderV2 from '../SingleJob/Layout/StickyCompanyHeader/StickyCompanyHeaderV2/StickyCompanyHeaderV2';
import { Loading } from './JobCard/Loading';
import { ButtonFooterV2 } from '../SingleJob/Layout/ButtonFooter/ButtonFooterV2';
import { buildMetrics } from './utils';
import { retrieveSearchTrackingCookieData } from '../searchEmitters';
import { EmitMetric } from '../../Analytics/VisibilityPixel/VisibilityPixel';
import { googleAnalytic } from '../../../pages/googeAnalyticHelper';
import { PageContext } from '../../../contexts/ContextCreator';
import { snowPlowPosistionMapper } from '../../../utilities/snowPlowConstants';

const getCTSJobName = (slug) => {
    const { placedHash, ctsName } = retrieveSearchTrackingCookieData();

    if (placedHash === slug && ctsName) {
        return [ctsName];
    }
    return null;
};

const StickyHeader = ({
    viewportRef,
    topApplyButtonGroupRef,
    bottomApplyButtonGroupRef,
    onJobSaveClick,
    singleJobDetail,
    handleQuickApplyClick,
    showDirectApply,
    handleCompanyApplyClick,
    userLiked,
    jobDetails,
    ...others
}) => {
    const [isStickyVisible, setIsStickyVisible] = useState(false);
    const { isSM } = useViewport();
    const initialTopOffsetRef = useRef();

    useEffect(() => {
        let cancel = false;

        const scrollEvent = () => {
            const el = viewportRef.current;
            if (el) {
                if (!initialTopOffsetRef?.current) {
                    if (isSM) {
                        initialTopOffsetRef.current = 266;
                    } else {
                        initialTopOffsetRef.current = 122;
                    }
                }
            }

            const topApplyButtonGroupEl = topApplyButtonGroupRef?.current;
            const bottomApplyButtonGroupEl = bottomApplyButtonGroupRef?.current;

            if (topApplyButtonGroupEl && bottomApplyButtonGroupEl) {
                const topOffset = topApplyButtonGroupEl?.getBoundingClientRect()
                    ?.bottom;
                const isTopVisible = topOffset > initialTopOffsetRef.current;
                const bottomOffset = bottomApplyButtonGroupEl?.getBoundingClientRect()
                    ?.top;

                const viewportHeight = window.innerHeight;
                const isBottomVisible = bottomOffset < viewportHeight;
                if (cancel) return;
                setIsStickyVisible(!(isTopVisible || isBottomVisible));
            }
        };

        window.addEventListener('scroll', scrollEvent, true);

        return () => {
            cancel = true;
            window.removeEventListener('scroll', scrollEvent);
        };
    }, [bottomApplyButtonGroupRef, isSM, topApplyButtonGroupRef, viewportRef]);

    // recheck scroll position in like
    useEffect(() => {
        viewportRef?.current?.dispatchEvent(new Event('scroll'));
    }, [userLiked, viewportRef]);

    if (isSM) {
        return (
            <StickyCompanyHeaderV2
                handleCompanyApplyClick={handleCompanyApplyClick}
                onJobSaveClick={onJobSaveClick}
                isInternalSticky
                visible={isStickyVisible}
                {...singleJobDetail}
                showDirectApply={showDirectApply}
                handleQuickApplyClick={handleQuickApplyClick}
                saveButtonText="Save"
                applyButtonText="Apply"
            />
        );
    }

    if (!jobDetails) {
        return null;
    }

    return (
        <ButtonFooterV2
            handleCompanyApplyClick={handleCompanyApplyClick}
            handleQuickApplyClick={handleQuickApplyClick}
            onJobSaveClick={onJobSaveClick}
            userLiked={userLiked}
            jobDetails={jobDetails}
            visible={isStickyVisible}
            zIndex={10000}
            bottom={0}
            position="sticky"
            showDirectApply={showDirectApply}
            {...others}
        />
    );
};

const SingleJobCard = ({
    singleJobDetail,
    isSM,
    topApplyButtonGroupRef,
    bottomApplyButtonGroupRef,
    viewportRef,
    onJobSaveClick,
    showDirectApply,
    handleQuickApplyClick,
    handleCompanyApplyClick,
    searchQueryId,
}) =>
    useMemo(() => {
        if (singleJobDetail === JOB_CARD_LOADING) {
            return (
                <View
                    mt="30px"
                    ml="24px"
                    overflow="hidden"
                    height="calc(100vh - 148px)"
                >
                    <Loading />
                </View>
            );
        }

        if (singleJobDetail === JOBS_NOT_FOUND) {
            return (
                <View
                    mt="30px"
                    ml="24px"
                    overflow="hidden"
                    height="calc(100vh - 148px)"
                >
                    <JobNotFound
                        text="There are no jobs available for this search."
                        subtext="Enter another search term or location to keep searching."
                    />
                </View>
            );
        }

        return singleJobDetail ? (
            <>
                <View
                    px={isSM ? '24px' : '0px'}
                    pt={isSM ? '24px' : '0px'}
                    ref={viewportRef}
                >
                    <SingleJobTemplateV2
                        {...singleJobDetail}
                        topApplyButtonGroupRef={topApplyButtonGroupRef}
                        bottomApplyButtonGroupRef={bottomApplyButtonGroupRef}
                        onJobSaveClick={onJobSaveClick}
                        handleCompanyApplyClick={handleCompanyApplyClick}
                        handleQuickApplyClick={handleQuickApplyClick}
                        jobSearchVariant
                        showDirectApply={showDirectApply}
                        searchQueryId={searchQueryId}
                        isJobsFindPage
                    />
                </View>
                <StickyHeader
                    viewportRef={viewportRef}
                    topApplyButtonGroupRef={topApplyButtonGroupRef}
                    bottomApplyButtonGroupRef={bottomApplyButtonGroupRef}
                    onJobSaveClick={onJobSaveClick}
                    handleCompanyApplyClick={handleCompanyApplyClick}
                    handleQuickApplyClick={handleQuickApplyClick}
                    singleJobDetail={singleJobDetail}
                    showDirectApply={showDirectApply}
                />
            </>
        ) : (
            <View height="calc(100vh - 148px)">
                <JobNotFound />
            </View>
        );
    }, [
        handleCompanyApplyClick,
        bottomApplyButtonGroupRef,
        isSM,
        topApplyButtonGroupRef,
        onJobSaveClick,
        singleJobDetail,
        viewportRef,
        searchQueryId,
    ]);

export const JobCardWrapper = () => {
    const {
        singleJobDetail,
        onJobSaveClick,
        isMobileJobCardOpen,
        setIsMobileJobCardOpen,
        setHasApplied,
        setIsDirectApplyOpen,
        searchQueryId,
        isAuthenticatedUser,
        fullSearchQueryId,
    } = useJobSearchContext();
    const { setLoginWallWithDescriptionText } = useContext(PageContext);
    const topApplyButtonGroupRef = useRef();
    const bottomApplyButtonGroupRef = useRef();
    const viewportRef = useRef();
    const { isSM } = useViewport();

    const handleCompanyApplyClick = useCallback(
        (position = '') => {
            const clickMetrics = buildMetrics({
                type: 'misc_event_count',
                originalApplyUrl: singleJobDetail?.jobDetails?.applyUrl,
                category: singleJobDetail?.jobDetails.category,
                company: {
                    id: singleJobDetail?.company?.companyId,
                    name: singleJobDetail?.company?.companyName,
                },
                hash: singleJobDetail?.jobDetails?.slug,
                title: singleJobDetail?.jobDetails?.title,
                location: singleJobDetail?.jobDetails?.locationName,
                jobSearchQueryID: searchQueryId,
                position: null,
                miscEventType: `${position}-apply-job-button-click`,
                ctsName: getCTSJobName(singleJobDetail?.jobDetails?.slug),
                ctsEvent: 'APPLICATION_REDIRECT',
            });
            clickMetrics.snowPlow = {
                click: true,
                component: 'button',
                buttonType: 'apply',
                buttonText: 'Apply on company site',
                position: snowPlowPosistionMapper[position],
                targetPageSection: 'off_site',
                cardType: 'job',
                pageType: 'collection',
                pageSection: 'search',
                isSponsored: !!singleJobDetail?.jobDetails?.isSponsored,
                searchId: `${fullSearchQueryId}`,
                companyId: singleJobDetail?.company?.companyId,
                cardEntityId: singleJobDetail?.jobDetails?.slug,
            };
            EmitMetric(clickMetrics);
            googleAnalytic(
                'Click',
                'JobsV3',
                singleJobDetail?.company?.companyName
            );
            const newTab = window.open(
                singleJobDetail.jobDetails.applyUrl,
                '_blank'
            );
            newTab?.focus();
            if (!isAuthenticatedUser) {
                Cookies.set('EVENT-LOGIN-TRIGGER', 'job-search-page', {
                    expires: 1095,
                });
                setLoginWallWithDescriptionText({
                    title: 'Get jobs straight to your inbox',
                    description:
                        'See which companies want to hire more women like you.',
                });
                EmitMetric({
                    misc_event_type: 'after-apply-signup',
                    misc_event_count: 1,
                    test_flag: 'Job lander + Search',
                });
            }
        },
        [
            singleJobDetail,
            searchQueryId,
            isAuthenticatedUser,
            setLoginWallWithDescriptionText,
        ]
    );

    const handleQuickApplyClick = useCallback(
        async (position = '') => {
            setIsDirectApplyOpen(true);
            const clickMetrics = buildMetrics({
                type: 'misc_event_count',
                originalApplyUrl: singleJobDetail?.jobDetails?.applyUrl,
                category: singleJobDetail?.jobDetails.category,
                company: {
                    id: singleJobDetail?.company?.companyId,
                    name: singleJobDetail?.company?.companyName,
                },
                hash: singleJobDetail?.jobDetails?.slug,
                title: singleJobDetail?.jobDetails?.title,
                location: singleJobDetail?.jobDetails?.locationName,
                jobSearchQueryID: searchQueryId,
                position: null,
                miscEventType: `${position}-quick-apply-job-button-click`,
                ctsName: getCTSJobName(singleJobDetail?.jobDetails?.slug),
                ctsEvent: null,
            });
            clickMetrics.snowPlow = {
                click: true,
                component: 'button',
                buttonType: 'apply',
                buttonText: 'Quick Apply',
                targetPageSection: 'direct_apply_form',
                position: snowPlowPosistionMapper[position],
                isSponsored: !!singleJobDetail?.jobDetails?.isSponsored,
                cardType: 'job',
                pageType: 'collection',
                pageSection: 'search',
                searchId: `${fullSearchQueryId}`,
                companyId: singleJobDetail?.company?.companyId,
                cardEntityId: singleJobDetail?.jobDetails?.slug,
            };
            EmitMetric(clickMetrics);
        },
        [singleJobDetail, fullSearchQueryId, searchQueryId]
    );

    const handleOnJobSaveClick = useCallback(
        ({ userLiked, hash, posistion }) => {
            if (isAuthenticatedUser) {
                EmitMetric({
                    snowPlow: {
                        click: true,
                        component: 'button',
                        buttonType: 'job_save',
                        buttonText: !userLiked ? 'Save' : 'Unsave',
                        position: snowPlowPosistionMapper[posistion],
                        targetPageSection: 'search',
                        isSponsored: !!singleJobDetail?.jobDetails?.isSponsored,
                        cardType: 'job',
                        pageType: 'collection',
                        pageSection: 'search',
                        searchId: `${fullSearchQueryId}`,
                        companyId: singleJobDetail?.company?.companyId,
                        cardEntityId: singleJobDetail?.jobDetails?.slug,
                    },
                });

                onJobSaveClick({ userLiked, hash });
            }
        },
        [onJobSaveClick, isAuthenticatedUser]
    );

    const bodyRef = useRef(document?.querySelector('body'));

    const handlers = useSwipeable({
        onSwipedRight: () => setIsMobileJobCardOpen(false),
    });

    // Prevent body from scrolling when modal is open
    const [preventRootScrolling, setPreventRootScrolling] = useState(false);

    useEffect(() => {
        if (isMobileJobCardOpen && !preventRootScrolling) {
            setPreventRootScrolling(true);
            return;
        }

        if (preventRootScrolling && !isMobileJobCardOpen) {
            setPreventRootScrolling(false);
        }
    }, [isMobileJobCardOpen, preventRootScrolling]);

    useEffect(() => {
        const root = document.getElementById('root');
        if (preventRootScrolling && root) {
            root.style.overflow = 'hidden';
        }
        if (!preventRootScrolling && root) {
            root?.style?.removeProperty('overflow');
        }
        return () => root?.style?.removeProperty('overflow');
    }, [preventRootScrolling]);

    useEffect(() => {
        if (singleJobDetail?.jobDetails) {
            const applied = localStorage.getItem(
                `directSubmitted${singleJobDetail?.jobDetails?.slug}`
            );
            setHasApplied(!!applied);
        }
    }, [singleJobDetail]);

    // MOBILE RENDER
    if (!isSM) {
        const jobDetails = { slug: singleJobDetail?.jobDetails?.slug };
        const userLiked = singleJobDetail?.userLiked;

        return (
            <Drawer
                id="mobile-job-page"
                useFocusTrap={false}
                isOpen={isMobileJobCardOpen}
                selector={bodyRef}
                fullWidth
                position="right"
                height="100%"
                css={{
                    fontFamily: "'Open Sans', sans-serif",
                    paddingBottom: 'env(safe-area-inset-bottom)',
                }}
            >
                <Button
                    pl="16px"
                    mb="6px"
                    mt="16px"
                    leftIcon={
                        <Icon
                            outlineColor="#374660"
                            variant="chevron_left_16"
                        />
                    }
                    size="large"
                    variant="secondaryText"
                    onPress={() => {
                        setIsMobileJobCardOpen(false);
                    }}
                >
                    Back
                </Button>
                <View
                    css={{
                        paddingBottom:
                            'calc(24px + env(safe-area-inset-bottom))',
                    }}
                >
                    <div {...handlers}>
                        <SingleJobCard
                            singleJobDetail={singleJobDetail}
                            isSM={isSM}
                            topApplyButtonGroupRef={topApplyButtonGroupRef}
                            bottomApplyButtonGroupRef={
                                bottomApplyButtonGroupRef
                            }
                            setHasApplied={setHasApplied}
                            isAuthenticatedUser={isAuthenticatedUser}
                            handleQuickApplyClick={handleQuickApplyClick}
                            showDirectApply={
                                singleJobDetail?.jobDetails?.showDirectApply
                            }
                            viewportRef={viewportRef}
                            onJobSaveClick={handleOnJobSaveClick}
                            handleCompanyApplyClick={handleCompanyApplyClick}
                            searchQueryId={searchQueryId}
                        />
                    </div>
                </View>
                <StickyHeader
                    viewportRef={viewportRef}
                    topApplyButtonGroupRef={topApplyButtonGroupRef}
                    bottomApplyButtonGroupRef={bottomApplyButtonGroupRef}
                    onJobSaveClick={handleOnJobSaveClick}
                    showDirectApply={
                        singleJobDetail?.jobDetails?.showDirectApply
                    }
                    handleCompanyApplyClick={handleCompanyApplyClick}
                    handleQuickApplyClick={handleQuickApplyClick}
                    userLiked={userLiked}
                    jobDetails={jobDetails}
                    css={{
                        paddingBottom:
                            'calc(26px + env(safe-area-inset-bottom))',
                    }}
                />
            </Drawer>
        );
    }

    return (
        <SingleJobCard
            searchQueryId={searchQueryId}
            singleJobDetail={singleJobDetail}
            isSM={isSM}
            setHasApplied={setHasApplied}
            isAuthenticatedUser={isAuthenticatedUser}
            topApplyButtonGroupRef={topApplyButtonGroupRef}
            bottomApplyButtonGroupRef={bottomApplyButtonGroupRef}
            handleCompanyApplyClick={handleCompanyApplyClick}
            handleQuickApplyClick={handleQuickApplyClick}
            viewportRef={viewportRef}
            onJobSaveClick={handleOnJobSaveClick}
            showDirectApply={singleJobDetail?.jobDetails?.showDirectApply}
        />
    );
};
